import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBKOAcao } from '@/hooks/useBKOAcao';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { aplicarEscopoTriagem } from '@/components/kanban/triagem/triagemPedidoUtils';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { posicaoNaFila } from '@/lib/filaTriagem';
import { manterPedidoNoFim } from '@/components/kanban/triagem/manterPedidoNoFim';
import { depoisDoClique } from '@/components/kanban/triagem/acaoTriagem';

export type BkoValidacao = 'pendente' | 'validado' | 'reprovado' | null;

export interface ItemTriagemFechado {
  item_id: string;
  produto: string;
  quantidade: number;
  plano_valor: number;
  subtotal: number;
}

export interface PedidoTriagemFechado {
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  consultor_nome: string;
  equipe_nome: string;
  entrada_coluna_em: string;
  valor_total: number;
  lead_origem_id: string | null;
  telefone: string | null;
  bko_validacao: BkoValidacao;
  cliente_id: string | null;
  /** Passado para o fim da fila com justificativa (padrão Manter). */
  manter_em: string | null;
  /** Dono do pedido CNPJ, distribuído no Pedido fechado (`fn_bko_contrato_distribuir`). */
  dono_id: string | null;
  dono_nome: string;
  itens: ItemTriagemFechado[];
}

export const ehItemFibraInstalacao = (produto: string) => /^FIBRA/.test(String(produto || '').toUpperCase().trim());

export const temFibraParaInstalar = (pedido: PedidoTriagemFechado) =>
  pedido.itens.some((i) => ehItemFibraInstalacao(i.produto));

export function useTriagemPedidoFechado() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { executarAcaoConsolidadaAsync } = useBKOAcao();
  const { profile } = useAuth();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const { data: pedidos = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-pedido-fechado', profile?.id],
    queryFn: async () => {
      let q = supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, cnpj, razao_social, consultor_nome, equipe_nome, produto, quantidade, plano_valor, subtotal, entrada_coluna_em, lead_origem_id, contato_real_telefone, telefone_principal, cliente_whatsapp, tipo_pessoa, documento, cliente_id, manter_em, bko_criacao_nome')
        .eq('coluna_atual', 'PEDIDO FECHADO')
        .eq('quadro_atual', 'CONTRATO_PRIME')
        .order('entrada_coluna_em', { ascending: true })
        .order('pedido_id', { ascending: true });
      // Escopo por papel — mesma régua do Kanban (BKO vê só as equipes mapeadas)
      q = await aplicarEscopoTriagem(q, profile);
      const { data, error } = await q;

      if (error) {
        onQueryError(error);
        throw error;
      }
      onQuerySuccess();

      const porPedido = new Map<string, PedidoTriagemFechado>();
      for (const item of (data || []) as any[]) {
        const subtotal = Number(item.subtotal) || (Number(item.plano_valor) || 0) * (Number(item.quantidade) || 1);
        let pedido = porPedido.get(item.pedido_id);
        if (!pedido) {
          pedido = {
            pedido_id: item.pedido_id,
            numero_pedido: String(item.numero_pedido || ''),
            cnpj: item.cnpj || '',
            tipo_pessoa: item.tipo_pessoa ?? null,
            documento: item.documento || item.cnpj || '',
            razao_social: item.razao_social || '',
            consultor_nome: item.consultor_nome || '',
            equipe_nome: item.equipe_nome || '',
            entrada_coluna_em: item.entrada_coluna_em || '',
            valor_total: 0,
            lead_origem_id: item.lead_origem_id || null,
            telefone: item.contato_real_telefone || item.telefone_principal || item.cliente_whatsapp || null,
            bko_validacao: null,
            cliente_id: item.cliente_id || null,
            manter_em: null,
            dono_id: null,
            dono_nome: item.bko_criacao_nome || '',
            itens: [],
          };
          porPedido.set(item.pedido_id, pedido);
        }
        if (item.entrada_coluna_em && (!pedido.entrada_coluna_em || item.entrada_coluna_em < pedido.entrada_coluna_em)) {
          pedido.entrada_coluna_em = item.entrada_coluna_em;
        }
        if (item.manter_em && (!pedido.manter_em || item.manter_em > pedido.manter_em)) {
          pedido.manter_em = item.manter_em;
        }
        pedido.valor_total += subtotal;
        pedido.itens.push({
          item_id: item.item_id,
          produto: item.produto || '',
          quantidade: Number(item.quantidade) || 0,
          plano_valor: Number(item.plano_valor) || 0,
          subtotal,
        });
      }

      // Conferência BKO (convergência) da fila inteira numa query só
      const ids = Array.from(porPedido.keys());
      if (ids.length > 0) {
        try {
          const { data: rows } = await (supabase as any)
            .from('pedidos')
            .select('id, convergencia, bko_criacao_id')
            .in('id', ids);
          for (const row of rows || []) {
            const pedido = porPedido.get(row.id);
            if (pedido) {
              pedido.bko_validacao = (row.convergencia?.bko_validacao as BkoValidacao) ?? null;
              pedido.dono_id = row.bko_criacao_id ?? null;
            }
          }
        } catch (err) {
          console.error('Erro ao buscar conferência BKO da fila:', err);
        }
      }

      // Fila FIFO: pedido mais antigo na coluna primeiro; o adiado pelo Manter
      // conta da hora em que foi adiado (vai para o fim).
      return Array.from(porPedido.values()).sort((a, b) =>
        posicaoNaFila(a.entrada_coluna_em, a.manter_em) - posicaoNaFila(b.entrada_coluna_em, b.manter_em)
      );
    },
    refetchInterval,
  });

  // Quantos pedidos ESTA pessoa já pegou e ainda não criou — o número que
  // aparece no aviso de "Pegou = é seu".
  const { data: meusEsperandoCriacao = 0 } = useQuery({
    queryKey: ['meus-pedidos-esperando-criacao', profile?.id],
    enabled: !!profile?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase
        .from('v_planilha_mae')
        .select('pedido_id')
        .eq('coluna_atual', 'CRIAÇÃO PEDIDO')
        .eq('quadro_atual', 'CONTRATO_PRIME')
        .eq('bko_id', profile!.id);
      if (error) throw error;
      return new Set((data || []).map((r) => (r as { pedido_id: string }).pedido_id)).size;
    },
  });

  const invalidateAllQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['triagem-pedido-fechado'] });
    queryClient.invalidateQueries({ queryKey: ['triagem-criacao-pedido'] });
    queryClient.invalidateQueries({ queryKey: ['meus-pedidos-esperando-criacao'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['planilha-itens'] });
    queryClient.invalidateQueries({ queryKey: ['v_planilha_mae'] });
  };

  // A linha sai (ou desce) assim que o banco confirma; a Criação, vizinha que
  // recebe o pedido pego, e o contador "esperando criação" se releem.
  const depoisDeAndar = (pedidoId: string, modo: 'sai' | 'desce') => {
    void queryClient.invalidateQueries({ queryKey: ['meus-pedidos-esperando-criacao'] });
    return depoisDoClique(queryClient, ['triagem-pedido-fechado', 'triagem-criacao-pedido'], { pedidoId }, modo);
  };

  // "Pegou = é seu" (Douglas, 18/08/2026): o dono do contrato nasce AQUI, no ato
  // de pegar — carimba bko_criacao_id + dt_criacao e só então move o pedido
  // inteiro para CRIAÇÃO PEDIDO (histórico + entrada_coluna_em preservados).
  // Se o movimento falhar, o carimbo é desfeito: pedido sem dono é pedido livre.
  const pegarECriar = useMutation({
    mutationFn: async (pedido: PedidoTriagemFechado) => {
      if (pedido.bko_validacao === 'pendente') {
        throw new Error('Conferência BKO pendente — abra o painel do pedido (seta) e Aprove ou Reprove antes de iniciar a criação.');
      }
      if (!profile?.id) {
        throw new Error('Sessão sem usuário — recarregue a página para pegar o pedido.');
      }

      const { error: pegarError } = await rpcTriagem('pegar_pedido_para_criacao', {
        p_pedido_id: pedido.pedido_id,
        p_bko_id: profile.id,
      });
      if (pegarError) {
        throw new Error(pegarError.message || 'Não foi possível pegar o pedido.');
      }

      try {
        await executarAcaoConsolidadaAsync({
          itensIds: pedido.itens.map((i) => i.item_id),
          pedidoId: pedido.pedido_id,
          colunaAtual: 'PEDIDO FECHADO',
          origemTriagem: true,
        });
      } catch (err) {
        await rpcTriagem('soltar_pedido_criacao', {
          p_pedido_id: pedido.pedido_id,
          p_bko_id: profile.id,
        }).then(() => undefined, () => undefined);
        throw err;
      }

      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido, itens: pedido.itens.length };
    },
    onSuccess: ({ pedidoId, numeroPedido, itens }) => {
      toast({
        title: 'Pedido é seu',
        description: `Pedido ${numeroPedido} (${itens} ite${itens !== 1 ? 'ns' : 'm'}) foi para a SUA fila de criação. A contagem começou agora.`,
      });
      void depoisDeAndar(pedidoId, 'sai');
    },
    onError: (error) => {
      toast({
        title: 'Não deu para pegar o pedido',
        description: error instanceof Error ? error.message : 'Atualize a tela e tente de novo.',
        variant: 'destructive',
      });
    },
  });

  /* CPF: o contrato é a auditoria por voz, feita aqui no Pedido fechado
     (Douglas 14/10/2026, opção A). Feita a auditoria, o pedido fica no nome de
     quem apertou e vai direto para o Input: no CPF não há biometria nem aceite. */
  const auditoriaCpfParaInput = useMutation({
    mutationFn: async ({ pedido, dataIso }: { pedido: PedidoTriagemFechado; dataIso?: string | null }) => {
      if (pedido.tipo_pessoa !== 'PF') {
        throw new Error('Só o pedido CPF vai do Pedido fechado direto para o Input.');
      }
      const { error } = await rpcTriagem('triagem_sem_biometria', {
        p_pedido_id: pedido.pedido_id,
        p_coluna_origem: 'PEDIDO FECHADO',
        p_coluna_destino: 'INPUT',
        ...(dataIso ? { p_data: dataIso } : {}),
      });
      if (error) throw new Error(error.message || 'Não foi possível mover o pedido.');
      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido, itens: pedido.itens.length };
    },
    onSuccess: ({ pedidoId, numeroPedido, itens }) => {
      toast({
        title: 'Auditoria feita · enviado para Input',
        description: `Pedido ${numeroPedido} (${itens} ite${itens !== 1 ? 'ns' : 'm'}) está no seu nome, no Input.`,
      });
      void depoisDeAndar(pedidoId, 'sai');
    },
    onError: (error) => {
      toast({
        title: 'O pedido não andou',
        description: error instanceof Error ? error.message : 'Atualize a tela e tente de novo.',
        variant: 'destructive',
      });
    },
  });

  const manterNoFim = useMutation({
    mutationFn: async ({ pedido, justificativa, prd }: { pedido: PedidoTriagemFechado; justificativa: string; prd?: string }) => {
      const r = await manterPedidoNoFim({ titulo: 'Triagem Pedido Fechado', pedido, justificativa, prd, coluna: 'PEDIDO FECHADO' });
      return { ...r, pedidoId: pedido.pedido_id };
    },
    onSuccess: ({ pedidoId, numeroPedido }) => {
      toast({
        title: 'Passado para o fim da fila',
        description: `Pedido ${numeroPedido} adiado com justificativa — o próximo da fila liberou.`,
      });
      void depoisDeAndar(pedidoId, 'desce');
    },
    onError: (error) => {
      toast({
        title: 'Erro ao passar para o fim da fila',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  return {
    pedidos,
    isLoading,
    refetch,
    invalidateAllQueries,
    meusEsperandoCriacao,
    manterNoFim: manterNoFim.mutateAsync,
    pegarECriar: pegarECriar.mutateAsync,
    auditoriaCpfParaInput: auditoriaCpfParaInput.mutateAsync,
  };
}
